import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Spin, message } from "antd";
import {
    DownOutlined,
    RightOutlined,
    PlayCircleOutlined,
    AlignLeftOutlined,
    CopyOutlined,
    CheckOutlined,
    CloseOutlined,
    CloseCircleFilled,
    PlusCircleOutlined,
    DeleteOutlined
} from "@ant-design/icons";
import { hasPageAccess } from "../../services/personaApi";
import { fieldReader } from "../../services/loanApi";
import { getAccessToken } from "../../services/authApi";
import { getLoanProgramLabel } from "../../utils/loanProgramUtil";


const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;
const EXCEPTION_RULE_MANAGER_PAGE = "LoanRuleExceptionManager";

const FIELD_OPTIONS = [
    "CLTV",
    "LTV",
    "DTI",
    "FICO",
    "DSCR",
    "LoanAmount",
    "Rate",
    "Term"
];

const OPERATOR_OPTIONS = [
    { value: "equals", label: "equals" },
    { value: ">", label: ">" },
    { value: ">=", label: ">=" },
    { value: "<", label: "<" },
    { value: "<=", label: "<=" }
];

const GROUP_CONNECTOR_OPTIONS = ["&&", "||"];
const EFFECTIVE_CONTEXT_NAMES = process.env.REACT_APP_EFFECTIVE_CONTEXT_NAMES?.split(",") || ["MILESTONE_COMPLETION_RULE", "MILESTONE_RULE"];
const PROGRAM_FIELD_ID = "CX.LOAN.PRODUCT.NAME";


const DEFAULT_PROGRAM_VALUES = {
    "Ally": "N/A",
    "Activator": "N/A",
    "Accelerator Income": "N/A",
    "Accelerator": "N/A",
    "Activator Prime": "N/A",
    "Accelerator DSCR": "N/A",
    "Accelerator 5-8": "N/A",
    "Super Jumbo": "N/A",
    "Activator ITIN": "N/A",
    "Accelerator ITIN": "N/A",
    "Ambassador": "N/A",
    "Accelerator Equity First": "No",
    "Ally Equity First": "No"
};

const DEFAULT_SEVERITY_MAPPING = {
    "No": "FATAL"
};

const RULE_TYPES = {
    EXPRESSION: "Expression",
    PROGRAM_LOGIC: "ProgramLogic"
};

const safeJsonPreview = (value) => {
    if (!value) return "{}";

    if (typeof value === "string") {
        try {
            return JSON.stringify(JSON.parse(value), null, 2);
        } catch {
            return value;
        }
    }

    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return "{}";
    }
};

const getExistingOverrideJson = (groupRule) => {
    if (!groupRule) return "";
    return (
        groupRule.overrideRuleDefinitionJson ||
        groupRule.overrideLogicJson ||
        groupRule.loanOverrideJson ||
        groupRule.detailsJson ||
        ""
    );
};

const normalizeRuleType = (ruleType) => {
    const normalized = String(ruleType || "").toLowerCase().replace(/[\s_-]/g, "");
    return normalized === "programlogic" ? RULE_TYPES.PROGRAM_LOGIC : RULE_TYPES.EXPRESSION;
};

const getRuleFieldId = (rule) => {
    if (!rule) return "";
    return String(rule.fieldId || rule.FieldID || rule.fieldID || "").trim();
};

const getFirstValue = (source, keys, fallback = "") => {
    if (!source) return fallback;
    const value = keys
        .map((key) => source?.[key])
        .find((candidate) => candidate !== undefined && candidate !== null && String(candidate).trim() !== "");

    return value !== undefined ? value : fallback;
};

const normalizeEffectiveProgramRules = (payload) => {
    // Keep the existing UI behavior when backend already returns grouped shape.
    if (
        payload &&
        Array.isArray(payload.ruleContexts) &&
        Array.isArray(payload.ruleGroups) &&
        Array.isArray(payload.ruleGroupRules)
    ) {
        return {
            ruleContexts: payload.ruleContexts || [],
            ruleGroups: payload.ruleGroups || [],
            ruleGroupRules: payload.ruleGroupRules || [],
            rules: Array.isArray(payload.rules) ? payload.rules : []
        };
    }

    const rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.items)
            ? payload.items
            : [];

    const contextsByKey = new Map();
    const groupsByKey = new Map();
    const rulesById = new Map();
    const ruleGroupRules = [];

    rows.forEach((row, index) => {
        const contextName = String(getFirstValue(row, ["contextName", "ContextName"], "Unknown Context")).trim();
        const contextId = Number(getFirstValue(row, ["ruleContextId", "RuleContextId", "contextId", "ContextId"], 0)) || index + 1;
        const contextKey = `${contextId}::${contextName}`;

        if (!contextsByKey.has(contextKey)) {
            contextsByKey.set(contextKey, {
                ruleContextId: contextId,
                contextName,
                isActive: getFirstValue(row, ["contextIsActive", "ContextIsActive", "isActive", "IsActive"], true)
            });
        }
        const groupName = String(getFirstValue(row, ["groupName", "GroupName"], "Unknown Group")).trim();
        const groupId = Number(getFirstValue(row, ["ruleGroupId", "RuleGroupId", "groupId", "GroupId"], 0)) || index + 1;
        const groupExecutionOrder = Number(getFirstValue(row, ["groupExecutionOrder", "GroupExecutionOrder", "executionOrder", "ExecutionOrder"], 0)) || 0;
        const groupKey = `${contextId}::${groupName.toLowerCase()}`;

        if (!groupsByKey.has(groupKey)) {
            groupsByKey.set(groupKey, {
                ruleGroupId: groupId,
                ruleContextId: contextId,
                groupName,
                executionOrder: groupExecutionOrder,
                isActive: getFirstValue(row, ["groupIsActive", "GroupIsActive", "isActive", "IsActive"], true)
            });
        }

        const normalizedGroup = groupsByKey.get(groupKey);

        const ruleId = Number(getFirstValue(row, ["ruleId", "RuleId"], 0)) || 0;
        const ruleName = String(getFirstValue(row, ["ruleName", "RuleName", "name", "Name"], ruleId ? `Rule ${ruleId}` : "Rule")).trim();
        const ruleType = String(getFirstValue(row, ["ruleType", "RuleType"], "Expression")).trim();
        const expression = String(getFirstValue(row, ["expression", "Expression", "translatedQuery", "TranslatedQuery", "userQuery", "UserQuery"], "")).trim();
        const fieldId = String(getFirstValue(row, ["fieldId", "FieldId", "FieldID", "fieldID"], "")).trim();
        const globalRuleDefinitionJson = String(getFirstValue(row, ["globalRuleDefinitionJson", "GlobalRuleDefinitionJson"], "")).trim();

        if (ruleId && !rulesById.has(ruleId)) {
            rulesById.set(ruleId, {
                ruleId,
                ruleName,
                ruleType,
                expression,
                translatedQuery: expression,
                userQuery: expression,
                fieldId,
                FieldID: fieldId,
                ruleDefinitionJson: globalRuleDefinitionJson || "{}"
            });
        }

        ruleGroupRules.push({
            id: Number(getFirstValue(row, ["ruleGroupRuleId", "RuleGroupRuleId", "id", "Id"], 0)) || index + 1,
            ruleGroupId: Number(normalizedGroup?.ruleGroupId || groupId),
            sourceRuleGroupId: groupId,
            ruleId,
            executionOrder: Number(getFirstValue(row, ["ruleExecutionOrder", "RuleExecutionOrder", "executionOrder", "ExecutionOrder"], 0)) || 0,
            isActive: getFirstValue(row, ["isActive", "IsActive"], true),
            isOverridden: Boolean(getFirstValue(row, ["isOverridden", "IsOverridden"], false)),
            globalRuleDefinitionJson,
            effectiveRuleDefinitionJson: String(getFirstValue(row, ["effectiveRuleDefinitionJson", "EffectiveRuleDefinitionJson"], "")).trim()
        });
    });

    return {
        ruleContexts: Array.from(contextsByKey.values()),
        ruleGroups: Array.from(groupsByKey.values()),
        ruleGroupRules,
        rules: Array.from(rulesById.values())
    };
};

const createConditionGroup = () => ({
    id: Date.now() + Math.random(),
    connector: "AND",
    field: "",
    operator: "=",
    value: ""
});

const formatExpressionValue = (value) => {
    if (value === null || value === undefined) return "null";

    const text = String(value).trim();
    if (!text) return "\"\"";

    if (text.includes("[") && text.includes("]")) {
        return text;
    }

    if (!Number.isNaN(Number(text))) {
        return text;
    }

    if (text.toLowerCase() === "true" || text.toLowerCase() === "false") {
        return text.toLowerCase();
    }

    return `\"${text.replace(/\"/g, "\\\"")}\"`;
};

const extractBracketTokens = (expression) => {
    if (!expression) return [];
    const regex = /\[([^\]]+)\]/g;
    const tokens = [];
    let match = regex.exec(expression);
    while (match) {
        const token = String(match[1] || "").trim();
        if (token) {
            tokens.push(token);
        }
        match = regex.exec(expression);
    }
    return [...new Set(tokens)];
};

const parsePrimitiveValue = (raw) => {
    const value = String(raw || "").trim();
    if (!value) return "";

    if ((value.startsWith("\"") && value.endsWith("\"")) || (value.startsWith("'") && value.endsWith("'"))) {
        return value.slice(1, -1);
    }

    if (value.toLowerCase() === "true") return true;
    if (value.toLowerCase() === "false") return false;
    if (!Number.isNaN(Number(value))) return Number(value);

    return value;
};

const normalizeProgramKey = (value) => String(value ?? "").trim().replace(/^['\"]|['\"]$/g, "");

const normalizeOperatorForUi = (operator) => {
    const normalized = String(operator || "").trim();
    if (normalized === "=" || normalized === "==") return "equals";
    return normalized;
};

const normalizeOperatorForExpression = (operator) => {
    const normalized = String(operator || "").trim();
    if (normalized === "equals" || normalized === "=") return "==";
    return normalized;
};

const normalizeOperatorForJson = (operator) => {
    const normalized = String(operator || "").trim();
    return normalized === "=" ? "equals" : normalized;
};

const normalizeConnectorForUi = (connector) => {
    const normalized = String(connector || "").trim().toUpperCase();
    return normalized === "OR" || normalized === "||" ? "||" : "&&";
};

const normalizeSearchText = (value) => String(value || "")
    .toLowerCase()
    .replace(/[\s_-]+/g, " ")
    .trim();

const getRuleIdValue = (source) => {
    const raw = source?.ruleId ?? source?.RuleId ?? source?.id ?? source?.Id;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? 0 : parsed;
};

const getRuleNameValue = (source) => String(
    source?.ruleName ?? source?.RuleName ?? source?.name ?? source?.Name ?? ""
).trim();

const parsePreEvaluationToGroups = (preEvaluation) => {
    const text = String(preEvaluation || "").trim();
    if (!text) return [];

    const tokens = text.split(/\s*(\&\&|\|\||\bAND\b|\bOR\b)\s*/i);
    const groups = [];

    const parseClause = (clause, connector = "&&") => {
        const match = String(clause || "").trim().match(/^\(?\s*\[([^\]]+)\]\s*(equals|==|=|!=|>=|>|<=|<)\s*(.+?)\s*\)?$/i);
        if (!match) return null;
        return {
            ...createConditionGroup(),
            connector,
            field: String(match[1] || "").trim(),
            operator: normalizeOperatorForUi(match[2]),
            value: parsePrimitiveValue(match[3])
        };
    };

    const first = parseClause(tokens[0], "&&");
    if (first) {
        groups.push(first);
    }

    for (let i = 1; i < tokens.length - 1; i += 2) {
        const rawConnector = String(tokens[i] || "&&").toUpperCase();
        const connector = rawConnector === "OR" || rawConnector === "||" ? "||" : "&&";
        const clause = tokens[i + 1];
        const parsed = parseClause(clause, connector);
        if (parsed) {
            groups.push(parsed);
        }
    }

    return groups;
};

const getStepBucket = (value) => {
    const normalized = String(value || "").trim().toLowerCase();
    if (!normalized) return "info";
    if (normalized.includes("fatal")) return "fatal";
    if (normalized.includes("error") || normalized.includes("exception")) return "error";
    if (normalized.includes("warn")) return "warning";
    if (["pass", "passed", "success", "succeeded", "ok", "true"].includes(normalized)) return "pass";
    if (["fail", "failed", "false"].includes(normalized)) return "error";
    return "info";
};

const createSummaryCounts = () => ({
    passCount: 0,
    warningCount: 0,
    fatalCount: 0,
    errorCount: 0
});

const normalizeSummaryCounts = (summary) => ({
    passCount: Number(summary?.passCount || summary?.passed || 0),
    warningCount: Number(summary?.warningCount || summary?.warnings || 0),
    fatalCount: Number(summary?.fatalCount || summary?.fatals || 0),
    errorCount: Number(summary?.errorCount || summary?.errors || 0)
});

const addSeverityToSummary = (summary, severity) => {
    const bucket = getStepBucket(severity);
    if (bucket === "pass") summary.passCount += 1;
    if (bucket === "warning") summary.warningCount += 1;
    if (bucket === "fatal") summary.fatalCount += 1;
    if (bucket === "error") summary.errorCount += 1;
};

const deriveOverallStatus = (summary, fallback = "PASS") => {
    if ((summary?.fatalCount || 0) > 0) return "FATAL";
    if ((summary?.errorCount || 0) > 0) return "ERROR";
    if ((summary?.warningCount || 0) > 0) return "WARNING";
    if ((summary?.passCount || 0) > 0) return "PASS";
    return String(fallback || "PASS").toUpperCase();
};

const buildProgramLogicView = (raw) => {
    const source = raw && typeof raw === "object" ? raw : {};

    const normalizeRule = (rule, index) => {
        const severity = String(
            rule?.severity ||
            rule?.status ||
            rule?.result ||
            (rule?.isPass === true ? "PASS" : rule?.isPass === false ? "FATAL" : "INFO")
        ).toUpperCase();

        const trace = Array.isArray(rule?.trace)
            ? rule.trace.map((item, traceIndex) => ({
                step: Number(item?.step || traceIndex + 1),
                description: String(item?.description || item?.stepName || ""),
                value: String(item?.value ?? "")
            }))
            : [];

        return {
            key: `rule-${rule?.ruleId || rule?.id || index + 1}-${index}`,
            ruleId: Number(rule?.ruleId || rule?.id || 0),
            ruleName: String(rule?.ruleName || rule?.name || `Rule ${index + 1}`),
            severity,
            message: String(rule?.message || rule?.detail || rule?.reason || ""),
            comparison: rule?.comparison && typeof rule.comparison === "object" ? {
                fieldName: String(rule.comparison.fieldName || ""),
                actualValue: String(rule.comparison.actualValue ?? ""),
                expectedValue: String(rule.comparison.expectedValue ?? ""),
                operator: String(rule.comparison.operator || "")
            } : null,
            trace
        };
    };

    const sourceGroups = Array.isArray(source?.groups) ? source.groups : [];
    let normalizedGroups = [];

    if (sourceGroups.length > 0) {
        normalizedGroups = sourceGroups.map((group, groupIndex) => {
            const rules = (Array.isArray(group?.rules) ? group.rules : []).map(normalizeRule);
            const computedSummary = rules.reduce((acc, rule) => {
                addSeverityToSummary(acc, rule.severity);
                return acc;
            }, createSummaryCounts());

            return {
                key: `group-${groupIndex}`,
                groupName: String(group?.groupName || group?.contextName || `Group ${groupIndex + 1}`),
                program: String(group?.program || source?.program || ""),
                summary: group?.summary ? normalizeSummaryCounts(group.summary) : computedSummary,
                rules
            };
        });
    } else {
        const fallbackRuleResults = Array.isArray(source?.ruleResults) ? source.ruleResults : [];
        const groupedMap = new Map();

        fallbackRuleResults.forEach((item, index) => {
            const groupName = String(item?.groupName || item?.contextName || item?.context || "Execution Results").trim() || "Execution Results";
            const existing = groupedMap.get(groupName) || {
                key: `group-${groupedMap.size}`,
                groupName,
                program: String(item?.program || source?.program || ""),
                summary: createSummaryCounts(),
                rules: []
            };

            const normalizedRule = normalizeRule(item, index);
            existing.rules.push(normalizedRule);
            addSeverityToSummary(existing.summary, normalizedRule.severity);
            groupedMap.set(groupName, existing);
        });

        if (groupedMap.size === 0) {
            const fallbackSeverity = source?.isPass === true ? "PASS" : source?.isPass === false ? "FATAL" : "INFO";
            const fallbackRule = normalizeRule({
                ruleId: 1,
                ruleName: "Execution",
                severity: source?.overallStatus || source?.status || source?.result || fallbackSeverity,
                message: source?.message || source?.errorMessage || "Program logic execution completed.",
                comparison: null,
                trace: []
            }, 0);

            const fallbackSummary = createSummaryCounts();
            addSeverityToSummary(fallbackSummary, fallbackRule.severity);

            groupedMap.set("Execution Results", {
                key: "group-0",
                groupName: "Execution Results",
                program: String(source?.program || ""),
                summary: fallbackSummary,
                rules: [fallbackRule]
            });
        }

        normalizedGroups = Array.from(groupedMap.values());
    }

    const computedOverallSummary = normalizedGroups.reduce((acc, group) => {
        acc.passCount += Number(group.summary?.passCount || 0);
        acc.warningCount += Number(group.summary?.warningCount || 0);
        acc.fatalCount += Number(group.summary?.fatalCount || 0);
        acc.errorCount += Number(group.summary?.errorCount || 0);
        return acc;
    }, createSummaryCounts());

    const overallSummary = source?.summary ? normalizeSummaryCounts(source.summary) : computedOverallSummary;
    const overallStatus = String(
        source?.overallStatus || source?.status || source?.result || deriveOverallStatus(overallSummary, "PASS")
    ).toUpperCase();

    const totalRules = normalizedGroups.reduce((acc, group) => acc + (Array.isArray(group.rules) ? group.rules.length : 0), 0);

    return {
        overallStatus,
        summary: overallSummary,
        groups: normalizedGroups,
        totalRules,
        errorMessage: String(source?.errorMessage || "")
    };
};

const LoanRuleExceptionManager = ({ loanId = "", loanNumber = "" }) => {

    const [userName, setUserName] = useState("");
    const [canManageExceptions, setCanManageExceptions] = useState(false);
    const [accessChecked, setAccessChecked] = useState(false);
    const [permissionMessage, setPermissionMessage] = useState("");
    const [contextLoanId, setContextLoanId] = useState("");
    const [contextLoanNumber, setContextLoanNumber] = useState("");
    const [loading, setLoading] = useState(false);
    const [savingOverride, setSavingOverride] = useState(false);
    const [testingRule, setTestingRule] = useState(false);
    const [testRuleResult, setTestRuleResult] = useState(null);
    const [searchText, setSearchText] = useState("");
    const [programRulesData, setProgramRulesData] = useState({ ruleContexts: [], ruleGroups: [], ruleGroupRules: [], rules: [] });
    const [rules, setRules] = useState([]);
    const [expandedContexts, setExpandedContexts] = useState({});
    const [expandedGroups, setExpandedGroups] = useState({});
    const [overrideModalOpen, setOverrideModalOpen] = useState(false);
    const [selectedRule, setSelectedRule] = useState(null);
    const [overrideLogicJson, setOverrideLogicJson] = useState("");
    const [overrideReason, setOverrideReason] = useState("");
    const [reasonError, setReasonError] = useState("");
    const [overrideEditorMode, setOverrideEditorMode] = useState("json");
    const [conditionGroups, setConditionGroups] = useState([createConditionGroup()]);
    const [fieldCatalog, setFieldCatalog] = useState([]);
    const [programValues, setProgramValues] = useState(DEFAULT_PROGRAM_VALUES);
    const [severityRows, setSeverityRows] = useState([{ id: 1, key: "No", value: "FATAL" }]);
    const [loanProgramName, setLoanProgramName] = useState("");
    const syncingFromJsonRef = useRef(false);
    const [copiedState, setCopiedState] = useState({ original: false, override: false });
    const [testResultSearchText, setTestResultSearchText] = useState("");
    const [testResultFilter, setTestResultFilter] = useState("all");
    const [expandedResultGroups, setExpandedResultGroups] = useState({});
    const [expandedResultRules, setExpandedResultRules] = useState({});
    const [isTestResultCollapsed, setIsTestResultCollapsed] = useState(false);
    const [contextTestModalOpen, setContextTestModalOpen] = useState(false);
    const [selectedContextForTest, setSelectedContextForTest] = useState(null);

    const fieldSelectOptions = useMemo(() => {
        const options = (fieldCatalog || [])
            .map((field) => {
                const fieldName = String(field?.fieldName || "").trim();
                const dataType = String(field?.dataType || "").trim();

                if (!fieldName) return null;

                return {
                    value: fieldName,
                    label: dataType ? `${fieldName} (${dataType})` : fieldName
                };
            })
            .filter(Boolean)
            .sort((left, right) => left.value.localeCompare(right.value));

        if (options.length > 0) {
            return options;
        }

        return FIELD_OPTIONS.map((fieldName) => ({ value: fieldName, label: fieldName }));
    }, [fieldCatalog]);

    const conditionFieldSelectOptions = useMemo(() => {
        const base = [...fieldSelectOptions];
        const seen = new Set(base.map((option) => option.value));

        conditionGroups.forEach((group) => {
            const groupField = String(group?.field || "").trim();
            if (groupField && !seen.has(groupField)) {
                base.push({ value: groupField, label: `${groupField} (from preEvaluation)` });
                seen.add(groupField);
            }
        });

        return base;
    }, [conditionGroups, fieldSelectOptions]);

    const fieldsByName = useMemo(() => {
        const map = new Map();
        (fieldCatalog || []).forEach((f) => {
            const key = String(f?.fieldName || "").trim();
            if (key) {
                map.set(key, f);
            }
        });
        return map;
    }, [fieldCatalog]);

    const composedPreEvaluation = useMemo(() => {
        const activeGroups = conditionGroups
            .map((group) => ({
                ...group,
                field: String(group.field || "").trim(),
                operator: String(group.operator || "=").trim(),
                value: group.value
            }))
            .filter((group) => group.field && group.operator);

        return activeGroups.reduce((acc, group, index) => {
            const clause = `[${group.field}] ${normalizeOperatorForExpression(group.operator)} ${formatExpressionValue(group.value)}`;
            if (index === 0) return clause;
            return `${acc} ${normalizeConnectorForUi(group.connector)} ${clause}`;
        }, "");
    }, [conditionGroups]);

    const translatedPreEvaluation = useMemo(() => {
        if (!composedPreEvaluation) return "";

        return composedPreEvaluation.replace(/\[([^\]]+)\]/g, (_, name) => {
            const found = fieldsByName.get(String(name || "").trim());
            return found?.fieldId ? `[${found.fieldId}]` : `[${String(name || "").trim()}]`;
        });
    }, [composedPreEvaluation, fieldsByName]);

    const preEvaluationFieldMappings = useMemo(() => {
        const fields = extractBracketTokens(composedPreEvaluation);
        return fields.map((fieldName) => ({
            fieldName,
            fieldId: fieldsByName.get(fieldName)?.fieldId || fieldName
        }));
    }, [composedPreEvaluation, fieldsByName]);

    const selectedProgramName = useMemo(() => {
        const canonicalOrRaw = normalizeProgramKey(loanProgramName);
        if (!canonicalOrRaw) return "";

        const orgProgramName = normalizeProgramKey(getLoanProgramLabel(canonicalOrRaw) || canonicalOrRaw);
        const matchingExistingKey = Object.keys(programValues || {}).find(
            (key) => key.toLowerCase() === orgProgramName.toLowerCase()
        );

        return matchingExistingKey || orgProgramName;
    }, [loanProgramName, programValues]);

    const shouldShowProgramSections = Boolean(selectedProgramName);

    const selectedProgramValue = useMemo(() => {
        if (!selectedProgramName) return "";
        return programValues?.[selectedProgramName] ?? "";
    }, [programValues, selectedProgramName]);

    const programLogicResultView = useMemo(() => {
        if (!testRuleResult || testRuleResult.ruleType !== RULE_TYPES.PROGRAM_LOGIC) {
            return null;
        }

        return buildProgramLogicView(testRuleResult.raw || {});
    }, [testRuleResult]);

    const filteredProgramLogicGroups = useMemo(() => {
        if (!programLogicResultView) return [];

        const searchTerm = String(testResultSearchText || "").trim().toLowerCase();

        return (programLogicResultView.groups || [])
            .map((group) => {
                const groupMatched = !searchTerm || `${group.groupName} ${group.program}`.toLowerCase().includes(searchTerm);
                const rules = (group.rules || []).filter((rule) => {
                    const bucket = getStepBucket(rule.severity);
                    if (testResultFilter !== "all" && bucket !== testResultFilter) {
                        return false;
                    }

                    if (!searchTerm || groupMatched) {
                        return true;
                    }

                    const traceText = (rule.trace || [])
                        .map((traceItem) => `${traceItem.description} ${traceItem.value}`)
                        .join(" ");
                    const comparisonText = rule.comparison
                        ? `${rule.comparison.fieldName} ${rule.comparison.actualValue} ${rule.comparison.expectedValue} ${rule.comparison.operator}`
                        : "";
                    const haystack = [rule.ruleName, rule.message, rule.severity, traceText, comparisonText].join(" ").toLowerCase();
                    return haystack.includes(searchTerm);
                });

                if (rules.length === 0) {
                    return null;
                }

                const filteredSummary = rules.reduce((acc, rule) => {
                    addSeverityToSummary(acc, rule.severity);
                    return acc;
                }, createSummaryCounts());

                return {
                    ...group,
                    filteredSummary,
                    rules
                };
            })
            .filter(Boolean);
    }, [programLogicResultView, testResultFilter, testResultSearchText]);

    const filteredProgramLogicSummary = useMemo(() => {
        return filteredProgramLogicGroups.reduce((acc, group) => {
            acc.passCount += Number(group.filteredSummary?.passCount || 0);
            acc.warningCount += Number(group.filteredSummary?.warningCount || 0);
            acc.fatalCount += Number(group.filteredSummary?.fatalCount || 0);
            acc.errorCount += Number(group.filteredSummary?.errorCount || 0);
            return acc;
        }, createSummaryCounts());
    }, [filteredProgramLogicGroups]);

    const totalVisibleRules = useMemo(
        () => filteredProgramLogicGroups.reduce((acc, group) => acc + Number(group.rules?.length || 0), 0),
        [filteredProgramLogicGroups]
    );

    const fetchProgramRules = useCallback(async (resolvedLoanId) => {
        const response = await fetch(`${API_DOMAIN}/api/rules/program-rules/effective`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify({
                loanId: String(resolvedLoanId || ""),
                contextNames: EFFECTIVE_CONTEXT_NAMES
            })
        });

        if (!response.ok) {
            throw new Error("Failed to load program rules");
        }

        const data = await response.json();
        setProgramRulesData(normalizeEffectiveProgramRules(data));
    }, []);

    const fetchLoanProgramFromFieldReader = useCallback(async (resolvedLoanId) => {
        if (!resolvedLoanId) {
            setLoanProgramName("");
            return;
        }

        try {
            const authObject = await window.elli.script.getObject("auth");
            const authCode = await authObject.createAuthCode();
            const accessToken = await getAccessToken(authCode);
            const fieldValues = await fieldReader(accessToken, [PROGRAM_FIELD_ID], resolvedLoanId);
            setLoanProgramName(normalizeProgramKey(fieldValues?.[PROGRAM_FIELD_ID]));
        } catch {
            setLoanProgramName("");
        }
    }, []);

    const fetchRuleFields = useCallback(async () => {
        try {
            const response = await fetch(`${API_DOMAIN}/api/rules/rule-fields`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "x-api-key": process.env.REACT_APP_WEB_API_KEY || ""
                }
            });

            if (!response.ok) {
                throw new Error("Failed to load rule fields");
            }

            const data = await response.json();
            const mapped = (Array.isArray(data) ? data : []).map((field) => ({
                fieldId: String(field?.fieldId || field?.fieldKey || field?.id || "").trim(),
                fieldName: String(field?.fieldName || field?.displayName || field?.name || "").trim(),
                dataType: String(field?.dataType || field?.datatype || "").trim()
            })).filter((field) => field.fieldName);

            setFieldCatalog(mapped);
        } catch {
            setFieldCatalog([]);
        }
    }, []);

    const loadPageData = useCallback(async (resolvedLoanId) => {
        if (!resolvedLoanId) {
            return;
        }

        setLoading(true);
        try {
            await Promise.all([
                fetchProgramRules(resolvedLoanId),
                fetchRuleFields(),
                fetchLoanProgramFromFieldReader(resolvedLoanId)
            ]);
        } catch (err) {
            message.error(err?.message || "Failed to load rule exception data");
        } finally {
            setLoading(false);
        }
    }, [fetchLoanProgramFromFieldReader, fetchProgramRules, fetchRuleFields]);

    useEffect(() => {
        if (!accessChecked) return;

        if (!canManageExceptions) {
            setLoading(false);
            return;
        }

        const resolvedLoanId = loanId || contextLoanId;
        if (!resolvedLoanId) return;

        loadPageData(resolvedLoanId);
    }, [accessChecked, canManageExceptions, contextLoanId, loanId, loadPageData]);

    useEffect(() => {
        const loadLoanContext = async () => {
            try {
                if (!(window.elli && window.elli.script && window.elli.script.guest)) {
                    throw new Error("elli.script.guest is not available.");
                }

                window.elli.script.guest.create();

                const loanObject = await window.elli.script.getObject("loan");
                const loanPayload = await loanObject.all();
                const loan = loanPayload?.loan || loanPayload || {};
                const currentLoanId = loan?.id || await loanObject.getField("GUID");

                setContextLoanId(String(currentLoanId || ""));
                setContextLoanNumber(String(loan?.loanNumber || ""));


                const tpoAuthObject = await window.elli.script.getObject("auth");
                const user = await tpoAuthObject.getUser();
                const userData = user;

                if (!userData) {
                    setCanManageExceptions(false);
                    setPermissionMessage("You do not have access to this page.");
                    setAccessChecked(true);
                    return;
                }
                const userFullName = `${user.firstName || ""} ${user.lastName || ""}`.trim();
                setUserName(userFullName);

                const allowed = await hasPageAccess(EXCEPTION_RULE_MANAGER_PAGE, user);
                setCanManageExceptions(allowed);
                if (!allowed) {
                    setPermissionMessage("You do not have access to this page.");
                } else {
                    setPermissionMessage("");
                }
                setAccessChecked(true);

                // setContextLoanId('03e177ad-f871-4d9d-a271-708d6d63e023');
                // setContextLoanNumber('12608003436');
            } catch (err) {
                console.warn("Failed to load loan context from EllieMae", err);
                setCanManageExceptions(false);
                setPermissionMessage("You do not have access to this page.");
                setAccessChecked(true);
            }
        };

        loadLoanContext();
    }, []);

    const effectiveLoanId = loanId || contextLoanId;
    const effectiveLoanNumber = loanNumber || contextLoanNumber;

    const ruleById = useMemo(() => {
        const map = new Map();
        rules.forEach((rule) => {
            const id = getRuleIdValue(rule);
            if (id > 0) {
                map.set(id, rule);
            }
        });
        return map;
    }, [rules]);

    const programRuleById = useMemo(() => {
        const map = new Map();
        (programRulesData.rules || []).forEach((rule) => {
            const id = getRuleIdValue(rule);
            if (id > 0) {
                map.set(id, rule);
            }
        });
        return map;
    }, [programRulesData.rules]);

    const groupsByContext = useMemo(() => {
        const map = new Map();
        (programRulesData.ruleGroups || []).forEach((group) => {
            const key = Number(group.ruleContextId);
            const existing = map.get(key) || [];
            existing.push(group);
            map.set(key, existing);
        });

        map.forEach((value, key) => {
            map.set(
                key,
                value.sort((left, right) => Number(left.executionOrder || 0) - Number(right.executionOrder || 0))
            );
        });

        return map;
    }, [programRulesData.ruleGroups]);

    const rulesByGroup = useMemo(() => {
        const map = new Map();
        (programRulesData.ruleGroupRules || []).forEach((groupRule) => {
            const key = Number(groupRule.ruleGroupId);
            const existing = map.get(key) || [];
            existing.push(groupRule);
            map.set(key, existing);
        });

        map.forEach((value, key) => {
            map.set(
                key,
                value.sort((left, right) => Number(left.executionOrder || 0) - Number(right.executionOrder || 0))
            );
        });

        return map;
    }, [programRulesData.ruleGroupRules]);

    const searchResult = useMemo(() => {
        const term = normalizeSearchText(searchText);
        const contexts = programRulesData.ruleContexts || [];

        if (!term) {
            return {
                visibleContexts: contexts,
                matchedContextIds: [],
                matchedGroupIds: [],
                matchedGroupRuleIds: [],
                contextNameMatchedIds: [],
                groupNameMatchedIds: []
            };
        }

        const matchedContextIds = new Set();
        const matchedGroupIds = new Set();
        const matchedGroupRuleIds = new Set();
        const contextNameMatchedIds = new Set();
        const groupNameMatchedIds = new Set();

        const filtered = contexts.filter((context) => {
            const contextId = Number(context.ruleContextId);
            const contextName = normalizeSearchText(context.contextName);
            const groups = groupsByContext.get(contextId) || [];

            if (contextName.includes(term)) {
                matchedContextIds.add(contextId);
                contextNameMatchedIds.add(contextId);
                groups.forEach((group) => matchedGroupIds.add(Number(group.ruleGroupId)));
                return true;
            }

            const hasGroupOrRuleMatch = groups.some((group) => {
                const groupId = Number(group.ruleGroupId);
                const groupName = normalizeSearchText(group.groupName);
                const groupRules = rulesByGroup.get(groupId) || [];

                if (groupName.includes(term)) {
                    matchedGroupIds.add(groupId);
                    groupNameMatchedIds.add(groupId);
                    return true;
                }

                const groupMatched = groupRules.some((groupRule) => {
                    const ruleId = getRuleIdValue(groupRule);
                    const rule = programRuleById.get(ruleId) || ruleById.get(ruleId);
                    const ruleName = normalizeSearchText(getRuleNameValue(rule) || getRuleNameValue(groupRule));
                    const ruleIdText = String(ruleId || "");
                    const matched = ruleName.includes(term) || ruleIdText.includes(term);
                    if (matched) {
                        matchedGroupRuleIds.add(Number(groupRule.id));
                    }
                    return matched;
                });

                if (groupMatched) {
                    matchedGroupIds.add(groupId);
                }

                return groupMatched;
            });

            if (hasGroupOrRuleMatch) {
                matchedContextIds.add(contextId);
            }

            return hasGroupOrRuleMatch;
        });

        return {
            visibleContexts: filtered,
            matchedContextIds: [...matchedContextIds],
            matchedGroupIds: [...matchedGroupIds],
            matchedGroupRuleIds: [...matchedGroupRuleIds],
            contextNameMatchedIds: [...contextNameMatchedIds],
            groupNameMatchedIds: [...groupNameMatchedIds]
        };
    }, [groupsByContext, programRuleById, programRulesData.ruleContexts, ruleById, rulesByGroup, searchText]);

    const {
        visibleContexts,
        matchedContextIds,
        matchedGroupIds,
        matchedGroupRuleIds,
        contextNameMatchedIds,
        groupNameMatchedIds
    } = searchResult;

    useEffect(() => {
        const term = searchText.trim();
        if (!term) return;

        if (matchedContextIds.length > 0) {
            setExpandedContexts((prev) => {
                const next = { ...prev };
                matchedContextIds.forEach((contextId) => {
                    next[contextId] = true;
                });
                return next;
            });
        }

        if (matchedGroupIds.length > 0) {
            setExpandedGroups((prev) => {
                const next = { ...prev };
                matchedGroupIds.forEach((groupId) => {
                    next[groupId] = true;
                });
                return next;
            });
        }
    }, [matchedContextIds, matchedGroupIds, searchText]);

    useEffect(() => {
        if (!programLogicResultView) {
            setExpandedResultGroups({});
            setExpandedResultRules({});
            return;
        }

        const nextExpandedGroups = {};
        (programLogicResultView.groups || []).forEach((group) => {
            nextExpandedGroups[group.key] = true;
        });

        setExpandedResultGroups(nextExpandedGroups);
        setExpandedResultRules({});
    }, [programLogicResultView]);

    const toggleContext = (contextId) => {
        setExpandedContexts((prev) => ({
            ...prev,
            [contextId]: !prev[contextId]
        }));
    };

    const toggleGroup = (groupId) => {
        setExpandedGroups((prev) => ({
            ...prev,
            [groupId]: !prev[groupId]
        }));
    };

    const hydrateInputFormFromJson = useCallback((jsonText, fallbackToDefault = true) => {
        const parsed = (() => {
            try {
                return jsonText ? JSON.parse(jsonText) : null;
            } catch {
                return null;
            }
        })();

        if (!parsed || typeof parsed !== "object") {
            if (!fallbackToDefault) {
                return false;
            }
            setConditionGroups([{ ...createConditionGroup(), field: "CLTV", operator: ">", value: "55" }]);
            setProgramValues(DEFAULT_PROGRAM_VALUES);
            setSeverityRows([{ id: 1, key: "No", value: "FATAL" }]);
            return true;
        }

        const nextGroups = [{
            ...createConditionGroup(),
            id: 1,
            field: String(parsed.field || "CLTV"),
            operator: normalizeOperatorForUi(String(parsed.operator || ">")),
            value: parsed.value ?? ""
        }];

        const preEvaluationText =
            parsed.preEvaluation ??
            parsed.PreEvaluation ??
            parsed.preevaluation ??
            "";
        const parsedPreEvaluationGroups = parsePreEvaluationToGroups(preEvaluationText);

        // Prefer preEvaluation when present so all clauses are reflected in input rows.
        if (parsedPreEvaluationGroups.length > 0) {
            setConditionGroups(parsedPreEvaluationGroups);
        } else if (Array.isArray(parsed.filters)) {
            parsed.filters.forEach((f, index) => {
                nextGroups.push({
                    ...createConditionGroup(),
                    id: index + 2,
                    connector: "&&",
                    field: String(f?.field || ""),
                    operator: normalizeOperatorForUi(String(f?.operator || "=")),
                    value: f?.value ?? ""
                });
            });

            setConditionGroups(nextGroups);
        } else {
            setConditionGroups(nextGroups);
        }

        const mergedProgramValues = {
            ...DEFAULT_PROGRAM_VALUES,
            ...(parsed.programValues && typeof parsed.programValues === "object" ? parsed.programValues : {})
        };
        setProgramValues(mergedProgramValues);

        const severityEntries = Object.entries(
            parsed.severityMapping && typeof parsed.severityMapping === "object"
                ? parsed.severityMapping
                : DEFAULT_SEVERITY_MAPPING
        );
        setSeverityRows(
            severityEntries.length > 0
                ? severityEntries.map(([key, value], index) => ({ id: index + 1, key: String(key), value: String(value) }))
                : [{ id: 1, key: "No", value: "FATAL" }]
        );
        return true;
    }, []);

    const buildRuleDefinitionFromInput = useCallback(() => {
        const firstGroup = conditionGroups[0] || { field: "", operator: "=", value: "" };
        const activeGroupsCount = conditionGroups
            .map((group) => ({
                field: String(group.field || "").trim(),
                operator: String(group.operator || "").trim()
            }))
            .filter((group) => group.field && group.operator).length;

        const castedValue =
            typeof firstGroup.value === "string" && firstGroup.value.trim() !== "" && !Number.isNaN(Number(firstGroup.value))
                ? Number(firstGroup.value)
                : firstGroup.value;

        const booleanValueForMultipleGroups = (() => {
            return false;
            // if (typeof firstGroup.value === "boolean") return firstGroup.value;
            // return String(firstGroup.value || "").trim().toLowerCase() === "false" ? false : true;
        })();

        const severityMapping = severityRows.reduce((acc, row) => {
            const key = String(row.key || "").trim();
            const value = String(row.value || "").trim();
            if (key) {
                acc[key] = value || "FATAL";
            }
            return acc;
        }, {});

        const result = {
            field: String(firstGroup.field || ""),
            operator: normalizeOperatorForJson(String(firstGroup.operator || "=")),
            value: activeGroupsCount === 1 ? castedValue : booleanValueForMultipleGroups
        };

        if (shouldShowProgramSections && selectedProgramName) {
            result.programValues = {
                [selectedProgramName]: selectedProgramValue
            };
            result.severityMapping = Object.keys(severityMapping).length > 0 ? severityMapping : DEFAULT_SEVERITY_MAPPING;
        }

        const preEvaluationText = composedPreEvaluation;
        if (preEvaluationText && activeGroupsCount > 1) {
            result.preEvaluation = preEvaluationText;
        }

        return result;
    }, [composedPreEvaluation, conditionGroups, selectedProgramName, selectedProgramValue, severityRows, shouldShowProgramSections]);

    const addConditionGroup = () => {
        setConditionGroups((prev) => ([
            ...prev,
            createConditionGroup()
        ]));
    };

    const removeConditionGroup = (id) => {
        setConditionGroups((prev) => {
            if (prev.length === 1) {
                return [{ ...prev[0], field: "", operator: "=", value: "" }];
            }
            return prev.filter((group) => group.id !== id);
        });
    };

    const updateConditionGroup = (id, key, value) => {
        setConditionGroups((prev) => prev.map((group) => group.id === id ? { ...group, [key]: value } : group));
    };

    const setEditorMode = (mode) => {
        if (mode === overrideEditorMode) {
            return;
        }

        if (mode === "json" && overrideEditorMode === "input") {
            const generatedJson = JSON.stringify(buildRuleDefinitionFromInput(), null, 2);
            setOverrideLogicJson(generatedJson);
        }

        setOverrideEditorMode(mode);
    };

    const copyUsingLegacySelection = (textToCopy) => {
        const textArea = document.createElement("textarea");
        textArea.value = String(textToCopy || "");
        textArea.setAttribute("readonly", "");
        textArea.style.position = "fixed";
        textArea.style.top = "-1000px";
        textArea.style.left = "-1000px";

        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        let copied = false;
        try {
            copied = document.execCommand("copy");
        } catch {
            copied = false;
        }

        document.body.removeChild(textArea);
        return copied;
    };

    const markCopied = (key) => {
        setCopiedState((prev) => ({ ...prev, [key]: true }));
        window.setTimeout(() => {
            setCopiedState((prev) => ({ ...prev, [key]: false }));
        }, 1200);
    };

    const copyToClipboard = async (textToCopy, key) => {
        const text = String(textToCopy || "");
        let copied = false;

        try {
            if (window.isSecureContext && navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text);
                copied = true;
            }
        } catch {
            copied = false;
        }

        if (!copied) {
            copied = copyUsingLegacySelection(text);
        }

        if (copied) {
            markCopied(key);
            return;
        }

        message.error("Copy failed. Please copy manually.");
    };

    useEffect(() => {
        if (!overrideModalOpen || overrideEditorMode !== "input") return;
        if (syncingFromJsonRef.current) {
            syncingFromJsonRef.current = false;
            return;
        }

        const generatedJson = JSON.stringify(buildRuleDefinitionFromInput(), null, 2);
        setOverrideLogicJson(generatedJson);
    }, [
        buildRuleDefinitionFromInput,
        conditionGroups,
        overrideEditorMode,
        overrideModalOpen,
        programValues,
        severityRows,
        selectedProgramName,
        selectedProgramValue,
        shouldShowProgramSections
    ]);

    const openOverrideModal = (context, group, groupRule) => {
        if (!canManageExceptions) {
            message.warning(permissionMessage || "You do not have permission to manage rule exceptions.");
            return;
        }

        const ruleId = Number(groupRule.ruleId);
        const globalRule = programRuleById.get(ruleId) || ruleById.get(ruleId);
        const globalRuleDefinitionJson =
            groupRule?.globalRuleDefinitionJson ||
            groupRule?.GlobalRuleDefinitionJson ||
            globalRule?.globalRuleDefinitionJson ||
            globalRule?.GlobalRuleDefinitionJson ||
            globalRule?.ruleDefinitionJson ||
            "{}";
        const effectiveRuleDefinitionJson =
            groupRule?.effectiveRuleDefinitionJson ||
            groupRule?.EffectiveRuleDefinitionJson ||
            getExistingOverrideJson(groupRule);

        setSelectedRule({ context, group, groupRule, globalRule, globalRuleDefinitionJson, effectiveRuleDefinitionJson });
        const initialJson = safeJsonPreview(effectiveRuleDefinitionJson || globalRuleDefinitionJson || "{}");
        setOverrideLogicJson(initialJson);
        hydrateInputFormFromJson(initialJson, true);
        setOverrideEditorMode("json");
        setOverrideReason("");
        setReasonError("");
        setOverrideModalOpen(true);
    };

    const closeOverrideModal = () => {
        setOverrideModalOpen(false);
        setSelectedRule(null);
        setOverrideLogicJson("");
        setOverrideReason("");
        setReasonError("");
        setOverrideEditorMode("json");
        setTestRuleResult(null);
        setTestResultFilter("all");
        setTestResultSearchText("");
        setExpandedResultGroups({});
        setExpandedResultRules({});
        setIsTestResultCollapsed(false);
        setCopiedState({ original: false, override: false });
    };

    const syncLocalOverrideState = useCallback((target, isOverridden, effectiveRuleDefinitionJson = "") => {
        if (!target) return;

        setProgramRulesData((prev) => ({
            ...prev,
            ruleGroupRules: (prev.ruleGroupRules || []).map((groupRule) => {
                const sameContext = Number(groupRule.ruleContextId ?? target.context?.ruleContextId) === Number(target.context?.ruleContextId);
                const sameGroup = Number(groupRule.ruleGroupId) === Number(target.group?.ruleGroupId);
                const sameRule = Number(groupRule.ruleId) === Number(target.groupRule?.ruleId);

                if (!(sameContext && sameGroup && sameRule)) {
                    return groupRule;
                }

                return {
                    ...groupRule,
                    isOverridden,
                    effectiveRuleDefinitionJson: isOverridden ? effectiveRuleDefinitionJson : ""
                };
            })
        }));
    }, []);

    const saveOverride = async () => {
        if (!selectedRule) return;
        if (!canManageExceptions) {
            message.warning(permissionMessage || "You do not have permission to manage rule exceptions.");
            return;
        }
        if (!effectiveLoanId) {
            message.error("Loan Id is required to save override");
            return;
        }
        if (!overrideReason.trim()) {
            setReasonError("Please enter override reason.");
            message.destroy();
            message.error("Override reason is required");
            return;
        }
        setReasonError("");

        const ruleDefinitionJsonToSave = overrideEditorMode === "input"
            ? JSON.stringify(buildRuleDefinitionFromInput(), null, 2)
            : overrideLogicJson;

        if (overrideEditorMode === "input") {
            setOverrideLogicJson(ruleDefinitionJsonToSave);
        }

        try {
            JSON.parse(ruleDefinitionJsonToSave);
        } catch {
            message.error("Override JSON is not valid");
            return;
        }
        const payload = {
            loanId: String(effectiveLoanId),
            loanNumber: effectiveLoanNumber || null,
            ruleContextId: Number(selectedRule.context.ruleContextId),
            ruleGroupId: Number(selectedRule.groupRule.sourceRuleGroupId || selectedRule.groupRule.ruleGroupId || selectedRule.group.ruleGroupId),
            ruleId: Number(selectedRule.groupRule.ruleId),
            ruleDefinitionJson: ruleDefinitionJsonToSave,
            overriddenBy: userName || "Admin",
            reason: overrideReason.trim()
        };

        setSavingOverride(true);
        try {
            const response = await fetch(`${API_DOMAIN}/api/rules/program-rules/exceptions`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                let result = null;
                try {
                    result = await response.json();
                } catch {
                    result = null;
                }
                throw new Error(result?.detail || result?.message || "Failed to save override");
            }

            message.success("Exception override saved");
            syncLocalOverrideState(selectedRule, true, ruleDefinitionJsonToSave);
            closeOverrideModal();
        } catch (err) {
            message.error(err?.message || "Failed to save override");
        } finally {
            setSavingOverride(false);
        }
    };

    const removeOverride = async () => {
        if (!selectedRule) return;
        if (!canManageExceptions) {
            message.warning(permissionMessage || "You do not have permission to manage rule exceptions.");
            return;
        }
        if (!effectiveLoanId) {
            message.error("Loan Id is required to remove override");
            return;
        }
        const ruleId = Number(selectedRule.groupRule?.ruleId);
        if (!ruleId) {
            message.error("Rule Id is required to remove override");
            return;
        }


        setSavingOverride(true);
        try {
            const updatedBy = encodeURIComponent(userName || "Admin");
            const encodedLoanId = encodeURIComponent(String(effectiveLoanId));
            const response = await fetch(`${API_DOMAIN}/api/rules/program-rules/exceptions/${ruleId}/${encodedLoanId}?updatedBy=${updatedBy}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json"
                }
            });

            if (!response.ok) {
                let result = null;
                try {
                    result = await response.json();
                } catch {
                    result = null;
                }
                throw new Error(result?.detail || result?.message || "Failed to remove override");
            }

            message.success("Override removed");
            syncLocalOverrideState(selectedRule, false, "");
            closeOverrideModal();
        } catch (err) {
            message.error(err?.message || "Failed to remove override");
        } finally {
            setSavingOverride(false);
        }
    };

    const onTestRule = () => {
        const executeTestRule = async () => {
            if (!selectedRule) return;
            if (!canManageExceptions) {
                message.warning(permissionMessage || "You do not have permission to manage rule exceptions.");
                return;
            }
            if (!effectiveLoanId) {
                message.error("Loan Id is required to test rule");
                return;
            }
            const ruleId = Number(selectedRule.groupRule?.ruleId || selectedRule.globalRule?.ruleId || 0);
            if (!ruleId) {
                message.error("Rule Id is required to test rule");
                return;
            }

            const resolvedRuleType = normalizeRuleType(selectedRule.globalRule?.ruleType || selectedRule.groupRule?.ruleType);
            setTestingRule(true);

            try {
                if (resolvedRuleType === RULE_TYPES.PROGRAM_LOGIC) {
                    const payload = {
                        ContextName: EFFECTIVE_CONTEXT_NAMES,
                        RuleId: ruleId,
                        fields: null,
                        showFatalAsWarning: false
                    };
                    const response = await fetch(`${API_DOMAIN}/api/rules/run-rules-multi/${encodeURIComponent(String(effectiveLoanId))}`, {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json"
                        },
                        body: JSON.stringify(payload)
                    });

                    const apiResult = await response.json();
                    if (!response.ok) {
                        throw new Error(apiResult?.detail || apiResult?.message || "Program logic execution failed");
                    }

                    setTestRuleResult({
                        ruleType: RULE_TYPES.PROGRAM_LOGIC,
                        raw: apiResult
                    });
                    setTestResultFilter("all");
                    setTestResultSearchText("");
                    setExpandedResultGroups({});
                    setExpandedResultRules({});
                    setIsTestResultCollapsed(false);
                    message.success("Program logic executed successfully");
                    return;
                }

                const expression =
                    selectedRule.globalRule?.expression ||
                    selectedRule.globalRule?.translatedQuery ||
                    selectedRule.globalRule?.userQuery ||
                    "";

                if (!expression.trim()) {
                    message.error("Expression is required to run query");
                    return;
                }

                const payload = {
                    ruleId,
                    expression,
                    loanId: String(effectiveLoanId),
                    executedBy: userName || "Admin"
                };

                const response = await fetch(`${API_DOMAIN}/api/rules/run`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(payload)
                });

                const apiResult = await response.json();
                if (!response.ok) {
                    throw new Error(apiResult?.detail || apiResult?.message || "Execution failed");
                }

                setTestRuleResult({
                    ruleType: RULE_TYPES.EXPRESSION,
                    passed: apiResult?.isPass === true,
                    fieldValues: Object.entries(apiResult?.objectDictionaryFieldValues || {}).map(([key, value]) => ({
                        key,
                        value
                    })),
                    raw: apiResult
                });
                setTestResultFilter("all");
                setTestResultSearchText("");
                setExpandedResultGroups({});
                setExpandedResultRules({});
                setIsTestResultCollapsed(false);
                message.success("Execution saved successfully");
            } catch (err) {
                message.error(err?.message || "Test rule execution failed");
            } finally {
                setTestingRule(false);
            }
        };

        executeTestRule();
    };

    const closeContextTestModal = () => {
        setContextTestModalOpen(false);
        setSelectedContextForTest(null);
        setTestRuleResult(null);
        setTestResultFilter("all");
        setTestResultSearchText("");
        setExpandedResultGroups({});
        setExpandedResultRules({});
        setIsTestResultCollapsed(false);
    };

    const onContextTestRun = (context) => {
        const executeContextTest = async () => {
            if (!canManageExceptions) {
                message.warning(permissionMessage || "You do not have permission to manage rule exceptions.");
                return;
            }
            if (!effectiveLoanId) {
                message.error("Loan Id is required to test rule");
                return;
            }

            const contextName = String(context?.contextName || "").trim();
            if (!contextName) {
                message.error("Context name is required to test");
                return;
            }

            setSelectedContextForTest(context);
            setContextTestModalOpen(true);
            setTestRuleResult(null);
            setTestResultFilter("all");
            setTestResultSearchText("");
            setExpandedResultGroups({});
            setExpandedResultRules({});
            setIsTestResultCollapsed(false);
            setTestingRule(true);

            try {
                const payload = {
                    ContextName: [contextName],
                    RuleId: null,
                    fields: null,
                    showFatalAsWarning: false
                };

                const response = await fetch(`${API_DOMAIN}/api/rules/run-rules-multi/${encodeURIComponent(String(effectiveLoanId))}`, {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json"
                    },
                    body: JSON.stringify(payload)
                });

                const apiResult = await response.json();
                if (!response.ok) {
                    throw new Error(apiResult?.detail || apiResult?.message || "Program logic execution failed");
                }

                setTestRuleResult({
                    ruleType: RULE_TYPES.PROGRAM_LOGIC,
                    raw: apiResult
                });
                message.success("Context test executed successfully");
            } catch (err) {
                message.error(err?.message || "Context test execution failed");
            } finally {
                setTestingRule(false);
            }
        };

        executeContextTest();
    };

    const renderTestRuleResultCard = () => {
        if (!testRuleResult) return null;

        return (
            <div className="border border-[#DCE6F5] rounded-lg p-4 bg-[#FAFCFF]">
                <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-[#123A7B]">Test Rule Result</div>
                    <button
                        type="button"
                        onClick={() => setIsTestResultCollapsed((prev) => !prev)}
                        title={isTestResultCollapsed ? "Expand results" : "Collapse results"}
                        aria-label={isTestResultCollapsed ? "Expand results" : "Collapse results"}
                        className="h-8 px-2 rounded-md border border-[#CFE0F8] text-[#1E4DA1] hover:bg-[#F1F6FF] inline-flex items-center gap-1 text-xs font-semibold"
                    >
                        {isTestResultCollapsed ? <RightOutlined /> : <DownOutlined />}
                        {isTestResultCollapsed ? "Expand" : "Collapse"}
                    </button>
                </div>

                {!isTestResultCollapsed && (testRuleResult.ruleType === RULE_TYPES.EXPRESSION ? (
                    <div className="space-y-3">
                        <div className="text-sm text-[#334155]">
                            Execution Result:{" "}
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${testRuleResult.passed ? "text-green-700 bg-green-100" : "text-red-700 bg-red-100"}`}>
                                {testRuleResult.passed ? "PASS" : "FAIL"}
                            </span>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border border-[#DCE6F5] rounded-lg">
                                <thead>
                                    <tr className="bg-[#F4F8FF]">
                                        <th className="py-2 px-3 border-b border-[#DCE6F5] text-left text-xs text-[#3C5785]">Field ID</th>
                                        <th className="py-2 px-3 border-b border-[#DCE6F5] text-left text-xs text-[#3C5785]">Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(testRuleResult.fieldValues || []).map((fv) => (
                                        <tr key={fv.key} className="hover:bg-[#F9FBFF]">
                                            <td className="py-2 px-3 border-b border-[#EDF2FA] text-sm text-[#334155]">{fv.key}</td>
                                            <td className="py-2 px-3 border-b border-[#EDF2FA] text-sm text-[#334155]">{String(fv.value ?? "")}</td>
                                        </tr>
                                    ))}
                                    {(testRuleResult.fieldValues || []).length === 0 && (
                                        <tr>
                                            <td colSpan={2} className="py-3 px-3 text-sm text-[#64748B] text-center">No field values returned.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {(() => {
                            if (!programLogicResultView) return null;

                            const overallSummary = programLogicResultView.summary || createSummaryCounts();
                            const overallStatus = String(programLogicResultView.overallStatus || deriveOverallStatus(overallSummary, "PASS")).toUpperCase();
                            const totalRules = Number(programLogicResultView.totalRules || 0);
                            const totalForBar = Math.max(totalRules, 1);
                            const passPercent = Math.round((Number(overallSummary.passCount || 0) / totalForBar) * 100);
                            const warningPercent = Math.round((Number(overallSummary.warningCount || 0) / totalForBar) * 100);
                            const fatalErrorPercent = Math.round(((Number(overallSummary.fatalCount || 0) + Number(overallSummary.errorCount || 0)) / totalForBar) * 100);
                            const overallStatusTone = getStepBucket(overallStatus);

                            const filterButtonClass = (filterValue) => {
                                const active = testResultFilter === filterValue;
                                return `h-8 px-3 rounded-full text-xs font-semibold border ${active
                                    ? "bg-[#EAF2FF] border-[#1E4DA1] text-[#1E4DA1]"
                                    : "bg-white border-[#DCE6F5] text-[#415A82]"}`;
                            };

                            const severityBadgeClass = (severity) => {
                                const bucket = getStepBucket(severity);
                                if (bucket === "pass") return "text-green-700 bg-green-100";
                                if (bucket === "warning") return "text-amber-700 bg-amber-100";
                                if (bucket === "fatal" || bucket === "error") return "text-red-700 bg-red-100";
                                return "text-[#1E4DA1] bg-[#EAF2FF]";
                            };

                            const countBadgeClass = (bucket) => {
                                if (bucket === "pass") return "bg-green-100 text-green-700";
                                if (bucket === "warning") return "bg-amber-100 text-amber-700";
                                if (bucket === "fatal") return "bg-red-100 text-red-700";
                                if (bucket === "error") return "bg-blue-100 text-blue-700";
                                return "bg-[#EAF2FF] text-[#1E4DA1]";
                            };

                            return (
                                <>
                                    <div className="rounded-lg border border-[#DCE6F5] bg-white overflow-hidden">
                                        <div className="p-3 border-b border-[#E6EEF9] bg-gradient-to-r from-[#FBFDFF] to-[#F6FAFF]">
                                            <div className="text-lg font-semibold text-[#123A7B]">Rule validation results</div>
                                        </div>

                                        <div className="p-3 space-y-3">
                                            <div className="grid grid-cols-1 md:grid-cols-6 gap-2">
                                                <div className={`md:col-span-2 rounded-md border p-3 flex items-center gap-3 ${overallStatusTone === "fatal" || overallStatusTone === "error"
                                                    ? "border-[#FECACA] bg-[#FEF2F2]"
                                                    : overallStatusTone === "warning"
                                                        ? "border-[#FCD34D] bg-[#FFFBEB]"
                                                        : "border-[#BBF7D0] bg-[#F0FDF4]"}`}>
                                                    <div className={`h-12 w-12 rounded-full flex items-center justify-center text-white text-xl font-bold ${overallStatusTone === "fatal" || overallStatusTone === "error"
                                                        ? "bg-[#EF4444]"
                                                        : overallStatusTone === "warning"
                                                            ? "bg-[#F59E0B]"
                                                            : "bg-[#16A34A]"}`}>
                                                        {overallStatusTone === "pass" ? "✓" : "×"}
                                                    </div>
                                                    <div>
                                                        <div className="text-xs text-[#516B93] font-semibold">Overall Status</div>
                                                        <div className="text-3xl leading-none font-bold text-[#173F81]">{overallStatus}</div>
                                                    </div>
                                                </div>

                                                <div className="rounded-md border border-[#BBF7D0] bg-[#F0FDF4] p-2 text-center">
                                                    <div className="text-xs font-semibold text-[#166534]">Pass</div>
                                                    <div className="text-2xl font-bold text-[#166534]">{overallSummary.passCount}</div>
                                                </div>
                                                <div className="rounded-md border border-[#FECACA] bg-[#FEF2F2] p-2 text-center">
                                                    <div className="text-xs font-semibold text-[#B91C1C]">Fatal</div>
                                                    <div className="text-2xl font-bold text-[#B91C1C]">{overallSummary.fatalCount}</div>
                                                </div>
                                                <div className="rounded-md border border-[#FCD34D] bg-[#FFFBEB] p-2 text-center">
                                                    <div className="text-xs font-semibold text-[#B45309]">Warning</div>
                                                    <div className="text-2xl font-bold text-[#B45309]">{overallSummary.warningCount}</div>
                                                </div>
                                                <div className="rounded-md border border-[#BFDBFE] bg-[#EFF6FF] p-2 text-center">
                                                    <div className="text-xs font-semibold text-[#1D4ED8]">Error</div>
                                                    <div className="text-2xl font-bold text-[#1D4ED8]">{overallSummary.errorCount}</div>
                                                </div>
                                            </div>

                                            <div className="h-3 rounded-full bg-[#EDF2FA] overflow-hidden flex">
                                                <div className="bg-[#16A34A]" style={{ width: `${passPercent}%` }} />
                                                <div className="bg-[#F59E0B]" style={{ width: `${warningPercent}%` }} />
                                                <div className="bg-[#EF4444]" style={{ width: `${fatalErrorPercent}%` }} />
                                            </div>

                                            <div className="flex flex-wrap gap-2">
                                                <button type="button" className={filterButtonClass("all")} onClick={() => setTestResultFilter("all")}>All issues {programLogicResultView.totalRules}</button>
                                                <button type="button" className={filterButtonClass("fatal")} onClick={() => setTestResultFilter("fatal")}>Fatal {overallSummary.fatalCount}</button>
                                                <button type="button" className={filterButtonClass("warning")} onClick={() => setTestResultFilter("warning")}>Warning {overallSummary.warningCount}</button>
                                                <button type="button" className={filterButtonClass("pass")} onClick={() => setTestResultFilter("pass")}>Passed {overallSummary.passCount}</button>
                                                <button type="button" className={filterButtonClass("error")} onClick={() => setTestResultFilter("error")}>Error {overallSummary.errorCount}</button>
                                            </div>

                                            <div className="flex flex-col md:flex-row md:items-center gap-2 md:justify-between">
                                                <input
                                                    type="text"
                                                    value={testResultSearchText}
                                                    onChange={(e) => setTestResultSearchText(e.target.value)}
                                                    placeholder="Search rules..."
                                                    className="h-9 w-full md:w-[280px] border border-[#DCE6F5] rounded-md px-3 text-sm"
                                                />
                                                <div className="text-xs text-[#607A9F]">Showing {totalVisibleRules} of {programLogicResultView.totalRules} rules</div>
                                            </div>

                                            {programLogicResultView.errorMessage && (
                                                <div className="rounded-md border border-[#FECACA] bg-[#FEF2F2] p-3 text-sm text-[#991B1B]">
                                                    {programLogicResultView.errorMessage}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        {filteredProgramLogicGroups.map((group) => {
                                            const isGroupOpen = expandedResultGroups[group.key] !== false;
                                            return (
                                                <div key={group.key} className="rounded-lg border border-[#DCE6F5] bg-white overflow-hidden">
                                                    <button
                                                        type="button"
                                                        onClick={() => setExpandedResultGroups((prev) => ({ ...prev, [group.key]: !isGroupOpen }))}
                                                        className="w-full px-4 py-3 bg-[#F4F8FF] border-b border-[#E6EEF9] flex items-start justify-between text-left"
                                                    >
                                                        <div className="flex items-start gap-2">
                                                            {isGroupOpen ? <DownOutlined className="text-[#234C8D] mt-1" /> : <RightOutlined className="text-[#234C8D] mt-1" />}
                                                            <div>
                                                                <div className="text-sm font-semibold text-[#123A7B]">{group.groupName}</div>
                                                                <div className="text-xs text-[#607A9F]">Program: {group.program || selectedProgramName || loanProgramName || "-"}</div>
                                                            </div>
                                                        </div>

                                                        <div className="flex items-center gap-2 text-xs font-semibold">
                                                            <span className={`h-6 min-w-6 px-2 rounded-full inline-flex items-center justify-center ${countBadgeClass("pass")}`}>{group.filteredSummary.passCount}</span>
                                                            <span className={`h-6 min-w-6 px-2 rounded-full inline-flex items-center justify-center ${countBadgeClass("fatal")}`}>{group.filteredSummary.fatalCount}</span>
                                                            <span className={`h-6 min-w-6 px-2 rounded-full inline-flex items-center justify-center ${countBadgeClass("warning")}`}>{group.filteredSummary.warningCount}</span>
                                                            <span className={`h-6 min-w-6 px-2 rounded-full inline-flex items-center justify-center ${countBadgeClass("error")}`}>{group.filteredSummary.errorCount}</span>
                                                        </div>
                                                    </button>

                                                    {isGroupOpen && (
                                                        <div className="divide-y divide-[#EDF2FA]">
                                                            {group.rules.map((rule) => {
                                                                const isRuleOpen = expandedResultRules[rule.key] === true;
                                                                return (
                                                                    <div key={rule.key} className="px-4 py-2">
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setExpandedResultRules((prev) => ({ ...prev, [rule.key]: !isRuleOpen }))}
                                                                            className="w-full flex items-center justify-between gap-3 text-left py-1"
                                                                        >
                                                                            <div className="text-sm font-semibold text-[#173F81]">{rule.ruleName}</div>
                                                                            <div className="flex items-center gap-2">
                                                                                <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${severityBadgeClass(rule.severity)}`}>{rule.severity}</span>
                                                                                {isRuleOpen ? <DownOutlined className="text-[#607A9F] text-xs" /> : <RightOutlined className="text-[#607A9F] text-xs" />}
                                                                            </div>
                                                                        </button>

                                                                        {isRuleOpen && (
                                                                            <div className="mt-2 rounded-md border border-[#E3ECFA] bg-[#FBFDFF] p-3 space-y-3">
                                                                                {rule.message && <div className="text-sm text-[#334155]">{rule.message}</div>}

                                                                                {rule.comparison && (
                                                                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                                                                        <div><div className="text-[#607A9F]">Field</div><div className="font-semibold text-[#123A7B]">{rule.comparison.fieldName || "-"}</div></div>
                                                                                        <div><div className="text-[#607A9F]">Actual Value</div><div className="font-semibold text-[#123A7B]">{rule.comparison.actualValue || "-"}</div></div>
                                                                                        <div><div className="text-[#607A9F]">Expected Value</div><div className="font-semibold text-[#123A7B]">{rule.comparison.expectedValue || "-"}</div></div>
                                                                                        <div><div className="text-[#607A9F]">Operator</div><div className="font-semibold text-[#123A7B]">{rule.comparison.operator || "-"}</div></div>
                                                                                    </div>
                                                                                )}

                                                                                <div>
                                                                                    <div className="text-sm font-semibold text-[#123A7B] mb-1">Evaluation Trace</div>
                                                                                    <div className="overflow-x-auto border border-[#E3ECFA] rounded-md bg-white">
                                                                                        <table className="min-w-full text-xs">
                                                                                            <thead className="bg-[#F4F8FF]">
                                                                                                <tr>
                                                                                                    <th className="text-left px-2 py-1 text-[#607A9F]">#</th>
                                                                                                    <th className="text-left px-2 py-1 text-[#607A9F]">Step</th>
                                                                                                    <th className="text-left px-2 py-1 text-[#607A9F]">Value</th>
                                                                                                </tr>
                                                                                            </thead>
                                                                                            <tbody>
                                                                                                {(rule.trace || []).map((trace, index) => (
                                                                                                    <tr key={`${rule.key}-${trace.step}-${index}`} className="border-t border-[#EDF2FA]">
                                                                                                        <td className="px-2 py-1 text-[#334155]">{trace.step}</td>
                                                                                                        <td className="px-2 py-1 text-[#334155]">{trace.description || "-"}</td>
                                                                                                        <td className="px-2 py-1 text-[#334155]">{trace.value || "-"}</td>
                                                                                                    </tr>
                                                                                                ))}
                                                                                                {(rule.trace || []).length === 0 && (
                                                                                                    <tr>
                                                                                                        <td colSpan={3} className="px-2 py-2 text-center text-[#607A9F]">No trace details available.</td>
                                                                                                    </tr>
                                                                                                )}
                                                                                            </tbody>
                                                                                        </table>
                                                                                    </div>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}

                                        {filteredProgramLogicGroups.length === 0 && (
                                            <div className="rounded-md border border-[#DCE6F5] bg-white p-4 text-center text-sm text-[#607A9F]">
                                                No rules match the current filters.
                                            </div>
                                        )}
                                    </div>

                                    <div className="text-xs text-[#607A9F] border-t border-[#E3ECFA] pt-2">
                                        Saving records the final determination with {filteredProgramLogicSummary.warningCount + filteredProgramLogicSummary.fatalCount + filteredProgramLogicSummary.errorCount} open issues.
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                ))}
            </div>
        );
    };

    if (!accessChecked) {
        return (
            <div className="w-full min-h-[360px] flex items-center justify-center p-5">
                <Spin size="large" />
            </div>
        );
    }

    if (!canManageExceptions) {
        return (
            <div className="w-full min-h-[360px] flex items-center justify-center p-5">
                <div className="w-full max-w-xl rounded-xl border border-[#FECACA] bg-[#FEF2F2] p-6 text-center">
                    <CloseCircleFilled className="text-[34px] text-[#DC2626]" />
                    <div className="mt-3 text-lg font-semibold text-[#991B1B]">Access Denied</div>
                    <div className="mt-1 text-sm text-[#7F1D1D]">{permissionMessage || "You do not have access to this page."}</div>
                </div>
            </div>
        );
    }

    return (
        <div className="w-full p-5 space-y-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <h1 className="text-3xl font-bold text-[#163D82]">Loan Rule Exception Manager</h1>
                    <p className="text-sm text-[#4B6185]">
                        Manage rule contexts, groups and exceptions for this loan. Override rules when needed to continue with the loan process.
                    </p>
                    {!!permissionMessage && (
                        <p className="text-sm text-[#92400E] mt-2">{permissionMessage}</p>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    <div className="relative w-full md:w-[420px]">

                        <input
                            type="text"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            placeholder="Search by rule name or context..."
                            className="w-full h-11 pl-12 pr-10 rounded-lg border border-[#D8E2F0] text-sm"
                        />
                        {/* <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6E84A8] pointer-events-none z-10 leading-none">
                            <SearchOutlined className="text-[15px]" />
                        </span> */}
                        {searchText && (
                            <button
                                type="button"
                                onClick={() => setSearchText("")}
                                aria-label="Clear search"
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#7D93B3] hover:text-[#415A82]"
                            >
                                <CloseCircleFilled className="text-[15px]" />
                            </button>
                        )}
                    </div>
                    {/* <button
                        type="button"
                        title="Filter"
                        className="h-11 w-11 rounded-lg border border-[#D8E2F0] text-[#2E4A7E] hover:bg-[#F4F8FF]"
                    >
                        <FilterOutlined />
                    </button> */}
                </div>
            </div>

            {loading ? (
                <div className="py-12 flex justify-center">
                    <Spin size="large" />
                </div>
            ) : (
                <div className="space-y-3">
                    {visibleContexts.map((context) => {
                        const groups = groupsByContext.get(Number(context.ruleContextId)) || [];
                        const activeSearch = Boolean(normalizeSearchText(searchText));
                        const contextId = Number(context.ruleContextId);
                        const isContextNameMatch = activeSearch && contextNameMatchedIds.includes(contextId);
                        const visibleGroups = activeSearch && !isContextNameMatch
                            ? groups.filter((group) => {
                                const groupId = Number(group.ruleGroupId);
                                if (matchedGroupIds.includes(groupId)) return true;
                                const groupRules = rulesByGroup.get(groupId) || [];
                                return groupRules.some((groupRule) => matchedGroupRuleIds.includes(Number(groupRule.id)));
                            })
                            : groups;
                        const isOpen = expandedContexts[context.ruleContextId] !== false;
                        const totalContextRules = visibleGroups.reduce((sum, group) => {
                            const baseGroupRules = rulesByGroup.get(Number(group.ruleGroupId)) || [];
                            const isGroupNameMatch = activeSearch && groupNameMatchedIds.includes(Number(group.ruleGroupId));
                            const groupRules = activeSearch
                                ? (isContextNameMatch || isGroupNameMatch
                                    ? baseGroupRules
                                    : baseGroupRules.filter((groupRule) => matchedGroupRuleIds.includes(Number(groupRule.id))))
                                : baseGroupRules;
                            return sum + groupRules.length;
                        }, 0);

                        return (
                            <div key={context.ruleContextId} className="rounded-xl border border-[#DCE6F5] bg-white overflow-hidden">
                                <div className="w-full px-4 py-4 flex items-center justify-between hover:bg-[#F9FBFF]">
                                    <button
                                        type="button"
                                        onClick={() => toggleContext(context.ruleContextId)}
                                        className="flex-1 flex items-center gap-3 text-left"
                                    >
                                        {isOpen ? <DownOutlined className="text-[#234C8D]" /> : <RightOutlined className="text-[#234C8D]" />}
                                        <div className="text-xl font-semibold text-[#123A7B]">{context.contextName}</div>
                                        <span className="px-2 py-1 text-xs rounded-full bg-[#EAF2FF] text-[#1F5EC6]">{totalContextRules} Rules</span>
                                    </button>

                                    <div className="ml-3 flex items-center gap-2">
                                        <button
                                            type="button"
                                            onClick={() => onContextTestRun(context)}
                                            disabled={testingRule || !canManageExceptions}
                                            title={!canManageExceptions ? (permissionMessage || "View-only access") : "Run context test"}
                                            aria-label="Run context test"
                                            className="h-8 w-8 rounded-md border border-[#CFE0F8] text-[#1E4DA1] hover:bg-[#F1F6FF] inline-flex items-center justify-center disabled:opacity-50"
                                        >
                                            <PlayCircleOutlined />
                                        </button>

                                        <span
                                            className={`px-3 py-1 text-xs rounded-full ${Number(context.isActive) === 1 || context.isActive === true
                                                ? "bg-[#DDF8EA] text-[#118E4E]"
                                                : "bg-[#EDF1F7] text-[#627B9F]"
                                                }`}
                                        >
                                            {Number(context.isActive) === 1 || context.isActive === true ? "Active" : "Inactive"}
                                        </span>
                                    </div>
                                </div>

                                {isOpen && (
                                    <div className="px-4 pb-4 bg-[#FCFDFF] border-t border-[#E8EEF8] space-y-2">
                                        {visibleGroups.map((group) => {
                                            const baseGroupRules = rulesByGroup.get(Number(group.ruleGroupId)) || [];
                                            const isGroupNameMatch = activeSearch && groupNameMatchedIds.includes(Number(group.ruleGroupId));
                                            const groupRules = activeSearch
                                                ? (isContextNameMatch || isGroupNameMatch
                                                    ? baseGroupRules
                                                    : baseGroupRules.filter((groupRule) => matchedGroupRuleIds.includes(Number(groupRule.id))))
                                                : baseGroupRules;
                                            const groupOpen = expandedGroups[group.ruleGroupId] !== false;

                                            return (
                                                <div key={group.ruleGroupId} className="rounded-lg border border-[#DCE6F5] bg-white overflow-hidden mt-3">
                                                    <button
                                                        type="button"
                                                        onClick={() => toggleGroup(group.ruleGroupId)}
                                                        className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-[#F9FBFF]"
                                                    >
                                                        <div className="flex items-center gap-3">
                                                            {groupOpen ? <DownOutlined className="text-[#234C8D] text-sm" /> : <RightOutlined className="text-[#234C8D] text-sm" />}
                                                            <span className="h-7 w-7 rounded-full bg-[#EAF2FF] text-[#1F5EC6] text-sm inline-flex items-center justify-center font-semibold">
                                                                {group.executionOrder}
                                                            </span>
                                                            <div>
                                                                <div className="font-semibold text-[#133B7D]">{group.groupName}</div>
                                                                <div className="text-xs text-[#607A9F]">Execution Order: {group.executionOrder}</div>
                                                            </div>
                                                        </div>
                                                        <div className="flex items-center gap-2 text-sm text-[#6F86AA]">
                                                            <span>{groupRules.length} Rules</span>
                                                        </div>
                                                    </button>

                                                    {groupOpen && (
                                                        <div className="px-4 pb-4">
                                                            <div className="overflow-x-auto border border-[#E0E9F8] rounded-lg">
                                                                <table className="min-w-full">
                                                                    <thead className="bg-[#F4F8FF]">
                                                                        <tr>
                                                                            <th className="text-left px-4 py-3 text-xs font-semibold text-[#3C5785]">Execution Order</th>
                                                                            <th className="text-left px-4 py-3 text-xs font-semibold text-[#3C5785]">Rule Name and Type</th>
                                                                            <th className="text-left px-4 py-3 text-xs font-semibold text-[#3C5785]">Status</th>
                                                                            <th className="text-left px-4 py-3 text-xs font-semibold text-[#3C5785]">Effective State</th>
                                                                            <th className="text-left px-4 py-3 text-xs font-semibold text-[#3C5785]">Actions</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {groupRules.map((groupRule) => {
                                                                            const ruleId = Number(groupRule.ruleId);
                                                                            const rule = programRuleById.get(ruleId) || ruleById.get(ruleId);
                                                                            const isActive = Number(groupRule.isActive) === 1 || groupRule.isActive === true;
                                                                            const isOverridden = Boolean(groupRule?.isOverridden ?? groupRule?.IsOverridden);

                                                                            return (
                                                                                <tr key={groupRule.id} className="border-t border-[#EDF2FA]">
                                                                                    <td className="px-4 py-3">
                                                                                        <span className="h-8 min-w-8 px-2 rounded-full bg-[#EAF2FF] text-[#1F5EC6] text-sm inline-flex items-center justify-center font-semibold">
                                                                                            {groupRule.executionOrder}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="px-4 py-3">
                                                                                        <div className="font-medium text-[#173F81]">{rule?.ruleName || `Rule ${groupRule.ruleId}`}</div>
                                                                                        <div className="text-xs text-[#6781A7]">{rule?.ruleType || "Expression"}</div>
                                                                                    </td>
                                                                                    <td className="px-4 py-3">
                                                                                        <span className={`px-3 py-1 rounded-full text-xs ${isActive ? "bg-[#DDF8EA] text-[#118E4E]" : "bg-[#EDF1F7] text-[#627B9F]"}`}>
                                                                                            {isActive ? "Active" : "Inactive"}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="px-4 py-3">
                                                                                        <span className={`px-3 py-1 rounded-full text-xs ${isOverridden ? "bg-[#FFF1DB] text-[#D47A0A]" : "bg-[#EAF2FF] text-[#1F5EC6]"}`}>
                                                                                            {isOverridden ? "Overridden" : "Global Rule"}
                                                                                        </span>
                                                                                    </td>
                                                                                    <td className="px-4 py-3">
                                                                                        <button
                                                                                            type="button"
                                                                                            onClick={() => openOverrideModal(context, group, groupRule)}
                                                                                            disabled={!canManageExceptions}
                                                                                            title={!canManageExceptions ? (permissionMessage || "View-only access") : "Override rule"}
                                                                                            className="h-9 px-3 rounded-md border border-[#2D4E88] text-[#1A3F81] text-sm font-semibold hover:bg-[#EDF4FF] disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                        >
                                                                                            Override Rule
                                                                                        </button>
                                                                                    </td>
                                                                                </tr>
                                                                            );
                                                                        })}
                                                                        {groupRules.length === 0 && (
                                                                            <tr>
                                                                                <td colSpan={5} className="text-center text-[#7087AA] py-4">
                                                                                    No rules found in this group.
                                                                                </td>
                                                                            </tr>
                                                                        )}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}

                                        {visibleGroups.length === 0 && (
                                            <div className="text-sm text-[#7087AA] px-4 py-3 bg-white border border-[#DCE6F5] rounded-lg mt-3">
                                                No groups found for this context.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {visibleContexts.length === 0 && (
                        <div className="border border-[#DCE6F5] bg-white rounded-xl p-6 text-center text-[#7087AA]">
                            No contexts available.
                        </div>
                    )}
                </div>
            )}

            <Modal
                open={overrideModalOpen}
                onCancel={closeOverrideModal}
                width={920}
                footer={[
                    <div key="footer-layout" style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <button
                            key="remove"
                            onClick={removeOverride}
                            disabled={savingOverride || !canManageExceptions}
                            className="h-11 px-4 rounded-md border border-[#EF4444] text-[#DC2626] hover:bg-[#FEF2F2] disabled:opacity-50"
                        >
                            Remove Override and Revert
                        </button>

                        <div className="flex items-center gap-2">
                            <button
                                key="cancel"
                                onClick={closeOverrideModal}
                                disabled={savingOverride}
                                className="h-11 px-5 rounded-md border border-[#CBD5E1] text-[#334155] bg-white hover:bg-[#F8FAFC] disabled:opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                key="save"
                                onClick={saveOverride}
                                disabled={savingOverride || !canManageExceptions}
                                className="h-11 px-5 rounded-md border border-[#1E4DA1] bg-[#1E4DA1] text-white hover:bg-[#1A4288] disabled:opacity-50"
                            >
                                {savingOverride ? "Saving..." : "Save Exception Override"}
                            </button>
                            <button
                                key="test"
                                onClick={onTestRule}
                                disabled={savingOverride || testingRule || !canManageExceptions}
                                className="h-11 px-5 rounded-md border border-[#1E4DA1] text-[#1E4DA1] bg-white hover:bg-[#EDF4FF] disabled:opacity-50"
                            >
                                {testingRule ? "Testing..." : "Test Rule"}
                            </button>
                        </div>
                    </div>
                ]}
                closeIcon={<CloseOutlined />}
                title={
                    <div className="space-y-1">
                        <div className="text-[32px] leading-none font-bold text-[#123A7B]">Override Rule Logic</div>
                        <div className="text-sm text-[#587196]">
                            Loan ID: {effectiveLoanId || "-"} | Rule: {selectedRule?.globalRule?.ruleName || `Rule ${selectedRule?.groupRule?.ruleId || ""}`}
                        </div>
                    </div>
                }
            >
                {selectedRule && (
                    <div className="space-y-4">
                        <div className="border border-[#DCE6F5] rounded-lg p-4 bg-[#FAFCFF]">
                            <div className="font-semibold text-[#123A7B] mb-3">Rule Information (Read Only)</div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                                <div>
                                    <div className="text-[#5C7397]">Context</div>
                                    <div className="font-medium text-[#123A7B]">{selectedRule.context.contextName}</div>
                                </div>
                                <div>
                                    <div className="text-[#5C7397]">Group</div>
                                    <div className="font-medium text-[#123A7B]">{selectedRule.group.groupName}</div>
                                </div>
                                <div className="min-w-0">
                                    <div className="flex items-center justify-between">
                                        <div className="text-[#5C7397]">Global Logic Definition</div>
                                        <button
                                            type="button"
                                            onClick={() => copyToClipboard(safeJsonPreview(selectedRule.globalRuleDefinitionJson || "{}"), "original")}
                                            title={copiedState.original ? "Copied" : "Copy original JSON"}
                                            aria-label={copiedState.original ? "Copied" : "Copy original JSON"}
                                            className="h-8 w-8 rounded-md border border-[#CFE0F8] text-[#1E4DA1] hover:bg-[#F1F6FF] inline-flex items-center justify-center"
                                        >
                                            {copiedState.original ? <CheckOutlined /> : <CopyOutlined />}
                                        </button>
                                    </div>
                                    <div
                                        className="mt-1 rounded-md bg-[#EFF5FF] px-2 py-1.5 text-xs text-[#224576] w-full truncate"
                                        title={safeJsonPreview(selectedRule.globalRuleDefinitionJson || "{}")}
                                    >
                                        {safeJsonPreview(selectedRule.globalRuleDefinitionJson || "{}").replace(/\s+/g, " ").trim()}
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border border-[#DCE6F5] rounded-lg p-4 bg-white">
                            <div className="flex items-center justify-between mb-2">
                                <div className="font-semibold text-[#123A7B]">Override Logic</div>
                                <div className="flex items-center gap-2">
                                    <div className="inline-flex rounded-md border border-[#CFE0F8] overflow-hidden">
                                        <button
                                            type="button"
                                            onClick={() => setEditorMode("json")}
                                            className={`px-3 py-1.5 text-xs font-semibold ${overrideEditorMode === "json"
                                                ? "bg-[#1E4DA1] text-white"
                                                : "bg-white text-[#1E4DA1]"}`}
                                        >
                                            JSON
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setEditorMode("input")}
                                            className={`px-3 py-1.5 text-xs font-semibold ${overrideEditorMode === "input"
                                                ? "bg-[#1E4DA1] text-white"
                                                : "bg-white text-[#1E4DA1]"}`}
                                        >
                                            Input Fields
                                        </button>
                                    </div>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            const sourceJson = overrideEditorMode === "input"
                                                ? JSON.stringify(buildRuleDefinitionFromInput(), null, 2)
                                                : overrideLogicJson;
                                            copyToClipboard(sourceJson, "override");
                                        }}
                                        title={copiedState.override ? "Copied" : "Copy override JSON"}
                                        aria-label={copiedState.override ? "Copied" : "Copy override JSON"}
                                        className="h-8 w-8 rounded-md border border-[#CFE0F8] text-[#1E4DA1] hover:bg-[#F1F6FF] inline-flex items-center justify-center"
                                    >
                                        {copiedState.override ? <CheckOutlined /> : <CopyOutlined />}
                                    </button>

                                    <button
                                        type="button"
                                        onClick={() => {
                                            const sourceJson = overrideEditorMode === "input"
                                                ? JSON.stringify(buildRuleDefinitionFromInput(), null, 2)
                                                : overrideLogicJson;
                                            try {
                                                const formatted = JSON.stringify(JSON.parse(sourceJson || "{}"), null, 2);
                                                setOverrideLogicJson(formatted);
                                                if (overrideEditorMode === "input") {
                                                    hydrateInputFormFromJson(formatted);
                                                }
                                            } catch {
                                                message.error("Cannot format invalid JSON");
                                            }
                                        }}
                                        title="Format JSON"
                                        aria-label="Format JSON"
                                        className="h-8 w-8 rounded-md border border-[#CFE0F8] text-[#1E4DA1] hover:bg-[#F1F6FF] inline-flex items-center justify-center"
                                    >
                                        <AlignLeftOutlined />
                                    </button>
                                </div>
                            </div>

                            {overrideEditorMode === "json" ? (
                                <textarea
                                    rows={10}
                                    value={overrideLogicJson}
                                    onChange={(e) => {
                                        const nextValue = e.target.value;
                                        setOverrideLogicJson(nextValue);
                                        syncingFromJsonRef.current = true;
                                        const isValid = hydrateInputFormFromJson(nextValue, false);
                                        if (!isValid) {
                                            syncingFromJsonRef.current = false;
                                        }
                                    }}
                                    className="w-full border border-[#DCE6F5] rounded-md p-3 font-mono text-sm text-[#1E293B]"
                                />
                            ) : (
                                <div className="space-y-4">
                                    <div className="space-y-2 border border-[#E0E9F8] rounded-lg p-3 bg-[#FAFCFF]">
                                        <div className="font-semibold text-[#123A7B] text-sm">Condition Groups</div>
                                        <div className="text-xs text-[#607A9F]">Add multiple groups for multi-field checks. JSON filters and preEvaluation are generated from these groups.</div>

                                        {conditionGroups.map((group, index) => (
                                            <div key={group.id} className="grid grid-cols-12 gap-2 items-center">
                                                {index > 0 ? (
                                                    <select
                                                        value={group.connector}
                                                        onChange={(e) => updateConditionGroup(group.id, "connector", e.target.value)}
                                                        className="col-span-2 h-10 border border-[#DCE6F5] rounded px-2 text-sm"
                                                    >
                                                        {GROUP_CONNECTOR_OPTIONS.map((option) => (
                                                            <option key={option} value={option}>{option}</option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <div className="col-span-2 text-xs text-[#607A9F] font-semibold">Group 1</div>
                                                )}

                                                <select
                                                    value={group.field}
                                                    onChange={(e) => updateConditionGroup(group.id, "field", e.target.value)}
                                                    className="col-span-3 h-10 border border-[#DCE6F5] rounded px-2 text-sm"
                                                >
                                                    <option value="">Select field</option>
                                                    {conditionFieldSelectOptions.map((option) => (
                                                        <option key={option.value} value={option.value}>{option.label}</option>
                                                    ))}
                                                </select>
                                                <select
                                                    value={group.operator}
                                                    onChange={(e) => updateConditionGroup(group.id, "operator", e.target.value)}
                                                    className="col-span-3 h-10 border border-[#DCE6F5] rounded px-2 text-sm"
                                                >
                                                    {OPERATOR_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>{option.label}</option>
                                                    ))}
                                                </select>
                                                <input
                                                    type="text"
                                                    value={group.value}
                                                    onChange={(e) => updateConditionGroup(group.id, "value", e.target.value)}
                                                    placeholder="Value or [Field Name]"
                                                    className="col-span-3 h-10 border border-[#DCE6F5] rounded px-3 text-sm"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeConditionGroup(group.id)}
                                                    className="col-span-1 text-[#3D4B62] hover:text-[#111827]"
                                                    title="Remove group"
                                                >
                                                    <DeleteOutlined />
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={addConditionGroup}
                                            className="text-[#1E63DA] text-sm inline-flex items-center gap-1"
                                        >
                                            <PlusCircleOutlined />
                                            Add Group
                                        </button>

                                        <div className="rounded-md border border-[#DCE6F5] bg-white p-3">
                                            <div className="text-xs font-semibold text-[#123A7B]">Composed preEvaluation</div>
                                            <pre className="mt-1 text-xs text-[#334155] overflow-auto">{composedPreEvaluation || "(empty)"}</pre>

                                            <div className="mt-2 text-xs font-semibold text-[#123A7B]">Translated with Field IDs</div>
                                            <pre className="mt-1 text-xs text-[#334155] overflow-auto">{translatedPreEvaluation || "(empty)"}</pre>

                                            <div className="mt-2 text-xs font-semibold text-[#123A7B]">Field IDs used in expression</div>
                                            <div className="mt-1 overflow-x-auto">
                                                <table className="min-w-full text-xs border border-[#E0E9F8]">
                                                    <thead>
                                                        <tr className="bg-[#F4F8FF]">
                                                            <th className="text-left px-2 py-1 border-b border-[#E0E9F8]">Field Name</th>
                                                            <th className="text-left px-2 py-1 border-b border-[#E0E9F8]">Field ID</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {preEvaluationFieldMappings.map((item) => (
                                                            <tr key={`${item.fieldName}-${item.fieldId}`}>
                                                                <td className="px-2 py-1 border-b border-[#EDF2FA]">{item.fieldName}</td>
                                                                <td className="px-2 py-1 border-b border-[#EDF2FA]">{item.fieldId}</td>
                                                            </tr>
                                                        ))}
                                                        {preEvaluationFieldMappings.length === 0 && (
                                                            <tr>
                                                                <td colSpan={2} className="px-2 py-2 text-center text-[#607A9F]">No fields referenced yet.</td>
                                                            </tr>
                                                        )}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>

                                    {shouldShowProgramSections ? (
                                        <>
                                            <div>
                                                <div className="font-semibold text-[#123A7B] mb-2 text-sm">Program Values</div>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                    <div className="grid grid-cols-2 gap-2 items-center">
                                                        <div className="text-xs text-[#48638B]">{selectedProgramName}</div>
                                                        <input
                                                            type="text"
                                                            value={selectedProgramValue}
                                                            onChange={(e) => setProgramValues((prev) => ({
                                                                ...prev,
                                                                [selectedProgramName]: e.target.value
                                                            }))}
                                                            className="h-9 border border-[#DCE6F5] rounded px-2 text-sm"
                                                        />
                                                    </div>
                                                </div>
                                            </div>

                                            <div>
                                                <div className="font-semibold text-[#123A7B] mb-2 text-sm">Severity Mapping</div>
                                                <div className="space-y-2">
                                                    {severityRows.map((row) => (
                                                        <div key={row.id} className="grid grid-cols-12 gap-2 items-center">
                                                            <input
                                                                type="text"
                                                                value={row.key}
                                                                onChange={(e) => setSeverityRows((prev) => prev.map((item) => item.id === row.id ? { ...item, key: e.target.value } : item))}
                                                                placeholder="Value"
                                                                className="col-span-5 h-10 border border-[#DCE6F5] rounded px-3 text-sm"
                                                            />
                                                            <select
                                                                value={row.value}
                                                                onChange={(e) => setSeverityRows((prev) => prev.map((item) => item.id === row.id ? { ...item, value: e.target.value } : item))}
                                                                className="col-span-5 h-10 border border-[#DCE6F5] rounded px-2 text-sm"
                                                            >
                                                                <option value="FATAL">FATAL</option>
                                                                <option value="WARNING">WARNING</option>
                                                                <option value="PASS">PASS</option>
                                                            </select>
                                                            <button
                                                                type="button"
                                                                onClick={() => setSeverityRows((prev) => prev.length > 1 ? prev.filter((item) => item.id !== row.id) : [{ id: 1, key: "", value: "FATAL" }])}
                                                                className="col-span-2 text-[#3D4B62] hover:text-[#111827]"
                                                                title="Remove severity mapping"
                                                            >
                                                                <DeleteOutlined />
                                                            </button>
                                                        </div>
                                                    ))}
                                                    <button
                                                        type="button"
                                                        onClick={() => setSeverityRows((prev) => ([...prev, { id: Date.now(), key: "", value: "FATAL" }]))}
                                                        className="text-[#1E63DA] text-sm inline-flex items-center gap-1"
                                                    >
                                                        <PlusCircleOutlined />
                                                        Add Severity
                                                    </button>
                                                </div>
                                            </div>
                                        </>
                                    ) : (
                                        <div className="rounded-md border border-[#E0E9F8] bg-[#FAFCFF] p-3 text-xs text-[#607A9F]">
                                            Program Values and Severity Mapping are available only when the loan program field is populated.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div>
                            <label className="font-semibold text-[#123A7B]">
                                Override Reason <span className="text-red-600">*</span> <span className="text-[#7087AA] font-normal">(nvarchar(500))</span>
                            </label>
                            <textarea
                                rows={4}
                                maxLength={500}
                                value={overrideReason}
                                onChange={(e) => {
                                    const nextValue = e.target.value;
                                    setOverrideReason(nextValue);
                                    if (nextValue.trim()) {
                                        setReasonError("");
                                    }
                                }}
                                placeholder="Provide detailed justification for compliance audit trail..."
                                className={`mt-2 w-full border rounded-md p-3 text-sm ${reasonError ? "border-red-500" : "border-[#DCE6F5]"}`}
                            />
                            {reasonError && <div className="mt-1 text-xs text-red-600">{reasonError}</div>}
                            <div className="text-right text-xs text-[#7087AA] mt-1">{overrideReason.length} / 500</div>
                        </div>

                        <div>
                            <div className="font-semibold text-[#123A7B] mb-1">User and Audit Information</div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                                <div>
                                    <div className="text-[#5C7397]">Overridden By</div>
                                    <div className="border border-[#DCE6F5] bg-[#F8FBFF] rounded-md px-3 py-2 text-[#1F3E74]">
                                        {userName || "Admin"}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[#5C7397]">Override Date</div>
                                    <div className="border border-[#DCE6F5] bg-[#F8FBFF] rounded-md px-3 py-2 text-[#1F3E74]">
                                        {new Date().toLocaleString("en-US", {
                                            timeZone: "America/Los_Angeles",
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                            hour: "numeric",
                                            minute: "2-digit"
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {renderTestRuleResultCard()}
                    </div>
                )}
            </Modal>

            <Modal
                open={contextTestModalOpen}
                onCancel={closeContextTestModal}
                width={920}
                footer={null}
                closeIcon={<CloseOutlined />}
                title={
                    <div className="space-y-1">
                        <div className="text-[26px] leading-none font-bold text-[#123A7B]">Context Test Result</div>
                        <div className="text-sm text-[#587196]">
                            Loan ID: {effectiveLoanId || "-"} | Context: {selectedContextForTest?.contextName || "-"}
                        </div>
                    </div>
                }
            >
                <div className="space-y-3">
                    {testingRule && !testRuleResult && (
                        <div className="py-8 flex justify-center">
                            <Spin size="large" />
                        </div>
                    )}
                    {renderTestRuleResultCard()}
                </div>
            </Modal>
        </div>
    );
};

export default LoanRuleExceptionManager;
